import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { adminUpdateListing } from '../../util/api';

export const RESULT_PAGE_SIZE = 12;

// ================ Async Thunks ================ //

const queryListingsPayloadCreator = (queryParams, { extra: sdk, dispatch, rejectWithValue }) => {
  return sdk.listings
    .query(queryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryListingsThunk = createAsyncThunk(
  'app/AdminPanelPage/queryListings',
  queryListingsPayloadCreator
);

const toggleFeaturedPayloadCreator = async ({ listingId, featured }, { rejectWithValue }) => {
  try {
    await adminUpdateListing({ listingId, featured });
    return { listingId, featured };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const toggleFeaturedThunk = createAsyncThunk(
  'app/AdminPanelPage/toggleFeatured',
  toggleFeaturedPayloadCreator
);

// ================ Slice ================ //

const adminPanelPageSlice = createSlice({
  name: 'AdminPanelPage',
  initialState: {
    pagination: null,
    queryInProgress: false,
    queryError: null,
    currentPageResultIds: [],
    togglingListingId: null,
    toggleError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(queryListingsThunk.pending, state => {
        state.queryInProgress = true;
        state.queryError = null;
        state.currentPageResultIds = [];
      })
      .addCase(queryListingsThunk.fulfilled, (state, action) => {
        state.queryInProgress = false;
        state.currentPageResultIds = action.payload.data.data.map(l => l.id);
        state.pagination = action.payload.data.meta;
      })
      .addCase(queryListingsThunk.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryError = action.payload;
      });

    builder
      .addCase(toggleFeaturedThunk.pending, (state, action) => {
        state.togglingListingId = action.meta.arg.listingId;
        state.toggleError = null;
      })
      .addCase(toggleFeaturedThunk.fulfilled, state => {
        state.togglingListingId = null;
      })
      .addCase(toggleFeaturedThunk.rejected, (state, action) => {
        state.togglingListingId = null;
        state.toggleError = action.payload;
      });
  },
});

export default adminPanelPageSlice.reducer;

// ================ Helpers ================ //

export const buildImageQueryParams = config => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    include: ['images'],
    'fields.listing': ['title', 'price', 'state', 'publicData'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };
};

// ================ Load data ================ //

export const loadData = (params, search, config) => dispatch => {
  const urlParams = new URLSearchParams(search);
  const page = parseInt(urlParams.get('page') || '1', 10);
  const keywords = urlParams.get('keywords') || '';
  const featured = urlParams.get('featured') || 'all';

  const queryParams = {
    page,
    perPage: RESULT_PAGE_SIZE,
    ...buildImageQueryParams(config),
  };
  if (keywords) queryParams.keywords = keywords;
  if (featured === 'featured') queryParams.pub_featured = true;

  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(queryListingsThunk(queryParams)),
  ]);
};
